/**
 * Where a sound is, as heard from the floating-origin camera (audio-direction §2, D-011): distance and direction
 * are computed in float64 world space, then rotated into camera space (the camera looks down −Z, +X is right).
 * The distance picks a band (near: the full sound, distance low-passed and panned; distant 2–20 km: a muffled
 * thump plus a sensor tick; beyond: silent). Pure and allocation-free (the caller owns the output object).
 */
import { distanceGain } from './falloff'
import type { DistantConfig, FalloffConfig, SpatialConfig } from './sfxSchema'

export const BAND = Object.freeze({ near: 0, distant: 1, silent: 2 })
export type Band = (typeof BAND)[keyof typeof BAND]

/** The listener: float64 world position and camera orientation (unit quaternion). */
export interface Listener {
  x: number
  y: number
  z: number
  qx: number
  qy: number
  qz: number
  qw: number
}

export function createListener(): Listener {
  return { x: 0, y: 0, z: 0, qx: 0, qy: 0, qz: 0, qw: 1 }
}

/** Camera-relative placement of one source. */
export interface Placement {
  distance: number
  /** −1 (left) .. 1 (right), before the pan width. */
  pan: number
  /** 0 (in front or beside) .. 1 (straight behind). */
  behind: number
}

export function createPlacement(): Placement {
  return { distance: 0, pan: 0, behind: 0 }
}

/** Fills `out` with the placement of world point (x, y, z) relative to `l`. */
export function place(l: Listener, x: number, y: number, z: number, out: Placement): Placement {
  const dx = x - l.x
  const dy = y - l.y
  const dz = z - l.z
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
  out.distance = d
  if (!(d > 1e-6)) {
    out.pan = 0
    out.behind = 0
    return out
  }
  // v' = conj(q) · v · q (world → camera space).
  const qx = -l.qx
  const qy = -l.qy
  const qz = -l.qz
  const qw = l.qw
  const ix = qw * dx + qy * dz - qz * dy
  const iy = qw * dy + qz * dx - qx * dz
  const iz = qw * dz + qx * dy - qy * dx
  const iw = -qx * dx - qy * dy - qz * dz
  const lx = ix * qw + iw * -qx + iy * -qz - iz * -qy
  const lz = iz * qw + iw * -qz + ix * -qy - iy * -qx
  out.pan = lx / d
  out.behind = lz > 0 ? lz / d : 0
  return out
}

/** Which band a distance falls in. */
export function bandOf(distance: number, spatial: SpatialConfig, falloff: FalloffConfig): Band {
  if (!(distance < falloff.maxDistance)) return BAND.silent
  return distance < spatial.nearMaxDistance ? BAND.near : BAND.distant
}

/** Log-distance interpolation from `a` at `d0` to `b` at `d1` (clamped). */
function logLerp(d: number, d0: number, d1: number, a: number, b: number): number {
  if (d <= d0) return a
  if (d >= d1) return b
  const u = Math.log(d / d0) / Math.log(d1 / d0)
  return a * Math.pow(b / a, u)
}

/** Near band: the source's low-pass (Hz) at `distance`, darker when behind the camera. */
export function nearLowpass(distance: number, behind: number, spatial: SpatialConfig, falloff: FalloffConfig): number {
  const hz = logLerp(distance, falloff.refDistance, spatial.nearMaxDistance, spatial.lowpassNearHz, spatial.lowpassFarHz)
  return hz * (1 - behind * (1 - spatial.rearLowpassScale))
}

/** Near band gain (the inverse falloff). */
export function nearGain(distance: number, falloff: FalloffConfig): number {
  return distanceGain(distance, falloff)
}

/** Distant band gain of a thump at `distance` (0 beyond `maxDistance`). */
export function distantGain(distance: number, spatial: SpatialConfig, distant: DistantConfig, falloff: FalloffConfig): number {
  if (!(distance < falloff.maxDistance)) return 0
  return logLerp(distance, spatial.nearMaxDistance, falloff.maxDistance, distant.gainNear, distant.gainFar)
}

/** Final stereo pan for a placement (−1..1). */
export function panOf(p: Placement, spatial: SpatialConfig): number {
  const v = p.pan * spatial.panWidth
  return v < -1 ? -1 : v > 1 ? 1 : v
}
